import { useState, useCallback } from 'react';

/**
 * Custom undo/redo hook for form builder
 * Tracks history of state changes with configurable max depth
 *
 * @param {*} initialState - Initial state value
 * @param {number} maxHistory - Maximum number of history entries (default: 50)
 * @returns {Object} - { state, set, undo, redo, canUndo, canRedo, reset }
 */
const useUndoRedo = (initialState, maxHistory = 50) => {
  const [history, setHistory] = useState({
    past: [],
    present: initialState,
    future: []
  });

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  /**
   * Set new state and add to history
   * Creates a deep copy to prevent reference issues
   */
  const set = useCallback((newState) => {
    setHistory((current) => {
      // Deep copy the new state to prevent reference issues
      const stateCopy = JSON.parse(JSON.stringify(newState));

      // Add current present to past, set new present, clear future
      const newPast = [...current.past, current.present];

      // Limit history size by removing oldest entries
      const limitedPast = newPast.slice(-maxHistory);

      return {
        past: limitedPast,
        present: stateCopy,
        future: [] // Clear redo stack when new action is performed
      };
    });
  }, [maxHistory]);

  /**
   * Update state without creating history entry
   * Useful for auto-save or background operations
   */
  const updateWithoutHistory = useCallback((newState) => {
    setHistory((current) => ({
      ...current,
      present: JSON.parse(JSON.stringify(newState))
    }));
  }, []);

  /**
   * Undo last action
   */
  const undo = useCallback(() => {
    setHistory((current) => {
      if (current.past.length === 0) return current;

      const previous = current.past[current.past.length - 1];
      const newPast = current.past.slice(0, -1);

      return {
        past: newPast,
        present: previous,
        future: [current.present, ...current.future]
      };
    });
  }, []);

  /**
   * Redo previously undone action
   */
  const redo = useCallback(() => {
    setHistory((current) => {
      if (current.future.length === 0) return current;

      const next = current.future[0];
      const newFuture = current.future.slice(1);

      return {
        past: [...current.past, current.present],
        present: next,
        future: newFuture
      };
    });
  }, []);

  /**
   * Reset history to initial state
   */
  const reset = useCallback((newInitialState) => {
    setHistory({
      past: [],
      present: newInitialState || initialState,
      future: []
    });
  }, [initialState]);

  return {
    state: history.present,
    set,
    updateWithoutHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    reset
  };
};

export default useUndoRedo;
